// src/pages/upload_document/UploadLog.tsx
import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the Data Grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the Data Grid
import "../../styles/grid-styles.css";
import download from "downloadjs";
import { fileTypeFromBuffer } from "file-type";
import { FaDownload, FaEye, FaMagnifyingGlassChart } from "react-icons/fa6";
import { NotificationContext } from "../../components/NotificationPane";
import { destructureDocPath } from "../../util/docPath";
import { NotificationContextType, UploadLog as UploadLogType, UploadLogLoad } from "../../types";

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { useApi } from "../../hooks/useApi";
import {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridSizeChangedEvent,
  IMultiFilterParams,
  RowClassRules,
  RowDataUpdatedEvent,
} from "ag-grid-enterprise";
import { useColumnDefaults } from "../../hooks/useColumnDefaults";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../../util/AgGridFunctions";
import GridHeaderTooltip from "../../components/PersistentGrid/GridHeaderTooltip";

type Props = {
  /** bump this to trigger a re-fetch */
  refreshKey?: number;
};

export default function UploadLog({ refreshKey = 0 }: Props) {
  const { getUploadLog, getUploadLogLoads, getDocByHash, downloadFile } = useApi();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const columnDefaults = useColumnDefaults();
  const [rowData, setRowData] = useState<UploadLogType[]>([]);
  // the full text of the message cell the user clicked; null = pop-up closed
  const [openMessage, setOpenMessage] = useState<string | null>(null);
  // the Sheets pop-up: the file whose count was clicked, and its tables (null = still loading,
  // "error" = the fetch failed); null overall = pop-up closed
  // the row whose Inspect pop-up is open (QA views to come); null = closed
  const [inspectRow, setInspectRow] = useState<UploadLogType | null>(null);
  const [openLoads, setOpenLoads] = useState<{ filename: string; loads: UploadLogLoad[] | null | "error" } | null>(null);

  const showLoads = useCallback(
    async (row: UploadLogType) => {
      setOpenLoads({ filename: row.filename, loads: null });
      try {
        const loads = await getUploadLogLoads(row.sk);
        // ignore a late answer for a row the user has since closed or swapped away from
        setOpenLoads((cur) => (cur && cur.filename === row.filename ? { ...cur, loads } : cur));
      } catch (err) {
        console.error("Failed to fetch loaded tables", err);
        setOpenLoads((cur) => (cur && cur.filename === row.filename ? { ...cur, loads: "error" } : cur));
      }
    },
    [getUploadLogLoads]
  );

  // Download / Preview read the file from the hash library by this row's filehash (doc.file_store).
  // A file the store does not hold (yet) has none - say so.
  const withLibraryDoc = useCallback(
    async (row: UploadLogType, action: (doc: { filehash: string; doc_path: string; eb_filename: string }) => Promise<void> | void) => {
      try {
        const doc = await getDocByHash(row.filehash);
        if (!doc) {
          pushNotification("This file is not in the hash library (yet), so it cannot be downloaded or previewed", "warning");
          return;
        }
        await action(doc);
      } catch (err) {
        console.error("Upload log document action failed", err);
        pushNotification("Could not open this document", "error");
      }
    },
    [getDocByHash, pushNotification]
  );

  const downloadRow = useCallback(
    (row: UploadLogType) =>
      withLibraryDoc(row, async (doc) => {
        const { drive, dir, filename } = destructureDocPath(doc.doc_path);
        const bytes = await downloadFile(drive, dir, filename);
        const detected = await fileTypeFromBuffer(bytes);
        const finalName = doc.eb_filename?.trim() ? doc.eb_filename.trim() : filename;
        download(new Blob([bytes], { type: detected?.mime }), finalName);
        pushNotification(`File ${finalName} downloaded successfully`, "success");
      }),
    [withLibraryDoc, downloadFile, pushNotification]
  );

  const previewRow = useCallback(
    (row: UploadLogType) =>
      withLibraryDoc(row, (doc) => {
        window.open(`/preview/${doc.filehash}`, "_blank", "noopener,noreferrer");
      }),
    [withLibraryDoc]
  );

  useEffect(() => {
    (async () => {
      try {
        const log = await getUploadLog();
        setRowData(log);
      } catch (err) {
        console.error("Failed to fetch upload log", err);
        setRowData([]);
      }
    })();
  }, [getUploadLog, refreshKey]);

  const toLocal = useCallback((v?: string | null) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  }, []);

  // Per-column decoration (label, width, date handling) for columns we recognise. Anything the
  // proc returns that is not listed here still shows, with the field name as its header.
  const knownCols = useMemo<Record<string, Partial<ColDef>>>(
    () => ({
      // no fixed width: fitColumns below sizes it to the longest filename in the data
      filename: { headerName: "Filename", minWidth: 120 },
      // the file's extension, lower case (pdf / xlsx / edi ...) - doc.file_log_v.filetype
      filetype: { headerName: "Type", width: 90 },
      // No filter override here: it takes the grid's agMultiColumnFilter like the rest, which
      // also gives you a set-filter list of uploaders to pick from.
      emailaddress: { headerName: "Email/User", width: 180 },
      uploadtime: {
        headerName: "Uploaded",
        valueFormatter: (p: any) => toLocal(p.value),
        // ensure correct date sorting + default to DESC
        comparator: (a?: string | null, b?: string | null) => {
          const ta = a ? new Date(a).getTime() : -Infinity;
          const tb = b ? new Date(b).getTime() : -Infinity;
          return ta - tb; // ag-Grid flips for desc automatically
        },
        sort: "desc",
        sortIndex: 0,
        width: 190,
        sortable: true,
      },
      // THE status: the folder the file is in (doc.file_log.load_status) -
      // 1_input 2_wip 8_done 3_unrecognised 4_error 5_skip
      load_status: { headerName: "Status", width: 150 },
      loader_module: { headerName: "Loader", width: 120 },
      // how many sheets/tables of the file loaded - a file is one-to-many with its tables, so
      // there is no single Table or Rows value to show. Blank while a web upload is pending.
      // Click the count for the per-table list (tables, rows, status - failed ones included).
      sheets: {
        headerName: "Sheets",
        width: 90,
        type: "numericColumn",
        cellRenderer: (p: any) => {
          if (p.data == null || p.value == null) return "";
          return (
            <span
              role="button"
              title="Click to see the tables loaded from this file"
              style={{ color: "var(--chakra-colors-blue-600)", textDecoration: "underline", cursor: "pointer" }}
              onClick={() => showLoads(p.data)}
            >
              {p.value}
            </span>
          );
        },
      },
      sk: { headerName: "Session", width: 100, hide: true },
      message: {
        headerName: "Message",
        minWidth: 200,
        // A non-blank message is a link that opens the whole string in a pop-up - the cell only
        // ever shows the start of a long loader error.
        cellRenderer: (p: any) => {
          const text = p.value == null ? "" : String(p.value).trim();
          if (!text) return "";
          return (
            <span
              role="button"
              title="Click to view the full message"
              style={{ color: "var(--chakra-colors-blue-600)", textDecoration: "underline", cursor: "pointer" }}
              onClick={() => setOpenMessage(String(p.value))}
            >
              {text}
            </span>
          );
        },
      },
      filehash: { headerName: "File Hash", minWidth: 200, hide: true },
    }),
    [toLocal, showLoads]
  );

  // Whole-row colour by status (= folder): green loaded, red needs a person. 1_input / 2_wip
  // (still in progress) and 5_skip (ignored on purpose) stay uncoloured. Group rows get neither.
  const rowClassRules = useMemo<RowClassRules<UploadLogType>>(
    () => ({
      "upload-row-loaded": (p) => p.data?.load_status === "8_done",
      "upload-row-not-loaded": (p) =>
        p.data?.load_status === "3_unrecognised" || p.data?.load_status === "4_error",
    }),
    []
  );

  // Column sizing: every column fits its own content (so the filename is always fully visible),
  // then whatever width is left over goes to the message column (or the last visible column if
  // there is no message column) so the grid never shows blank space on the right. If the content
  // is wider than the grid, nothing is shrunk - the grid scrolls horizontally instead. Runs on
  // first render, on every data refresh, and when the grid itself is resized.
  const containerRef = useRef<HTMLDivElement>(null);
  const fitColumns = useCallback((api: GridApi) => {
    if (!containerRef.current) return;
    api.autoSizeAllColumns(false);
    const shown = api.getAllDisplayedColumns();
    if (shown.length === 0) return;
    const used = shown.reduce((sum, c) => sum + c.getActualWidth(), 0);
    const spare = containerRef.current.clientWidth - used - 20; // vertical scrollbar allowance
    if (spare <= 0) return;
    const target = shown.find((c) => c.getColId() === "message") ?? shown[shown.length - 1];
    api.setColumnWidths([{ key: target, newWidth: target.getActualWidth() + spare }]);
  }, []);
  const onFirstDataRendered = useCallback((e: FirstDataRenderedEvent) => fitColumns(e.api), [fitColumns]);
  const onRowDataUpdated = useCallback((e: RowDataUpdatedEvent) => fitColumns(e.api), [fitColumns]);
  const onGridSizeChanged = useCallback((e: GridSizeChangedEvent) => fitColumns(e.api), [fitColumns]);

  // The columns ARE the result set of `exec dbo.sp_web_grid__file_log__get`, in the order
  // the proc selects them - change the proc's column list and the grid follows with no frontend
  // edit. Until the first row arrives the known columns stand in, so the grid has headers.
  // Keyed on the column NAMES, not on rowData: rebuilding the defs on every refresh made the
  // grid re-create its columns and throw away the widths fitColumns had just set.
  const fieldKey = (rowData.length ? Object.keys(rowData[0]) : Object.keys(knownCols)).join("|");
  const columnDefs = useMemo(
    () =>
      EDB_CUSTOM_withHeaderTooltips([
        // same Download / Preview buttons as the Document Library's Actions column
        {
          colId: "actions",
          headerName: "Actions",
          headerTooltip: "Actions",
          width: import.meta.env.DEV ? 140 : 100,
          sortable: false,
          filter: false,
          suppressMenu: true,
          enableRowGroup: false,
          pinned: "left",
          cellStyle: { alignItems: "center", justifyContent: "center" },
          cellRenderer: (p: any) =>
            p.data == null ? null : (
              <>
                <Tooltip label="Download" hasArrow>
                  <IconButton icon={<FaDownload />} size="sm" aria-label="Download" onClick={() => downloadRow(p.data)}>
                    Download
                  </IconButton>
                </Tooltip>
                <Tooltip label="Preview" hasArrow>
                  <IconButton icon={<FaEye />} size="sm" aria-label="Preview" onClick={() => previewRow(p.data)}>
                    Preview
                  </IconButton>
                </Tooltip>
                {/* Work in progress: local dev server only, hidden on DEV and PRD builds. */}
                {import.meta.env.DEV && (
                  <Tooltip label="Inspect details" hasArrow>
                    <IconButton icon={<FaMagnifyingGlassChart />} size="sm" aria-label="Inspect details" onClick={() => setInspectRow(p.data)}>
                      Inspect details
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ),
        },
        ...fieldKey.split("|").map((field) => ({
          field,
          headerName: field,
          ...buildDefaultColDef(columnDefaults, field),
          ...(knownCols[field] ?? {}),
        })),
      ]),
    [fieldKey, columnDefaults, knownCols, downloadRow, previewRow]
  );

  // Same general grid configuration as the Document Library grid (DocLibraryComponent.tsx) -
  // floating filters, multi-column filter with clear/apply, row grouping, and its null-scrubbing
  // formatter. The floating filter row is also what makes the Email/User filter this page applies
  // for you in onGridReady visible and clearable, instead of the grid looking empty for no
  // apparent reason when you have no recent uploads.
  const defaultColDef = useMemo<ColDef>(
    () => ({
      ...buildDefaultColDef(columnDefaults),
      resizable: true,
      sortable: true,
      floatingFilter: true,
      enableRowGroup: true,
      width: 120,
      minWidth: 120,
      filter: "agMultiColumnFilter",
      filterParams: {
        buttons: ["clear", "apply"],
        suppressRemoveEntries: true,
      } as IMultiFilterParams,
      tooltipComponent: GridHeaderTooltip,
      valueFormatter: (p) => {
        const v = p.value;
        if (v == null || v === "null" || v === "undefined") return "";
        return String(v);
      },
    }),
    [columnDefaults]
  );

  // Total + filtered row count in the grid's own status bar, so "how many rows am I looking at,
  // and how many did my filters take out" is answered on screen - same panel the Document
  // Library uses.
  const statusBar = useMemo(
    () => ({ statusPanels: [{ statusPanel: "agTotalAndFilteredRowCountComponent" }] }),
    []
  );

  // The view's grain is (filename, filehash) and it exposes no sk - keying on data.sk gave every
  // row the id "undefined", which ag-Grid warns about and which made rows accumulate (rather than
  // replace) on each refresh.
  // the session's own sk (doc.file_log.sk) -- one per row, stable across refreshes; the old view
  // had no key and we made one up from filehash|filename
  const getRowId = useCallback(
    (params: { data: UploadLogType }) => `${params.data.sk ?? `${params.data.filehash}|${params.data.filename}`}`,
    []
  );

  return (
    // 100% (not 100vh) so the grid fits the space BaseTemplate.Body actually leaves it - at 100vh
    // the action bar above it pushed the grid's own footer below the fold, so the grid looked like
    // it stopped at whatever fitted on screen. Same wrapper as the Document Library grid.
    <div ref={containerRef} className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
      <AgGridReact
        statusBar={statusBar}
        tooltipShowDelay={200}
        rowData={rowData}
        columnDefs={columnDefs as any}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        rowClassRules={rowClassRules}
        onFirstDataRendered={onFirstDataRendered}
        onRowDataUpdated={onRowDataUpdated}
        onGridSizeChanged={onGridSizeChanged}
        rowGroupPanelShow={"always"}
        groupDisplayType={"groupRows"}
        headerHeight={32}
        rowHeight={32}
        floatingFiltersHeight={32}
        animateRows
      />

      {/* Inspect details: empty for now - the QA views for the row go in the body. No animation, so
          it opens at once. */}
      <Modal isOpen={inspectRow !== null} onClose={() => setInspectRow(null)} size="6xl" scrollBehavior="inside" motionPreset="none">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>Inspect details - {inspectRow?.filename}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} minH="50vh" />
        </ModalContent>
      </Modal>

      <Modal isOpen={openMessage !== null} onClose={() => setOpenMessage(null)} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", margin: 0 }}>
              {openMessage ?? ""}
            </pre>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={openLoads !== null} onClose={() => setOpenLoads(null)} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>Tables loaded - {openLoads?.filename}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {openLoads?.loads == null ? (
              <Spinner />
            ) : openLoads.loads === "error" ? (
              <Text color="red.600">Could not load the table list.</Text>
            ) : openLoads.loads.length === 0 ? (
              <Text>No tables were loaded from this file.</Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Table</Th>
                    <Th isNumeric>Rows</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {openLoads.loads.map((l) => (
                    <Tr key={l.sk} title={l.status !== "1_loaded" && l.message ? l.message : undefined}>
                      <Td>{l.tablename ?? ""}</Td>
                      <Td isNumeric>{l.numrows == null ? "" : l.numrows.toLocaleString()}</Td>
                      <Td color={l.status === "1_loaded" ? undefined : "red.600"}>{l.status ?? ""}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
